import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { BrowserRouter, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import {
  api,
  daysUntil,
  type Ad,
  type Art,
  type Asset,
  type BoardUser,
  type CardSet,
  type Event,
  type Finance,
  type Member,
  type Stats,
  type Todo,
  type Transaction,
  type User,
} from './api';
import { Icon, Sigil } from './ui';
import Login from './views/Login';
import Lykilord from './views/Lykilord';
import Yfirlit from './views/Yfirlit';
import Vidburdir from './views/Vidburdir';
import Felagar from './views/Felagar';
import Utgjold from './views/Utgjold';
import Utgafur from './views/Utgafur';
import Utgafa from './views/Utgafa';
import Eignir from './views/Eignir';
import Mot from './views/Mot';
import Verkefni from './views/Verkefni';
import Auglysingar from './views/Auglysingar';
import Myndir from './views/Myndir';

/* ══ DATA ════════════════════════════════════════════════════════════ */

/** How often an open board checks for what the others have written. Long
 *  enough to be cheap on a small server, short enough that nobody sits
 *  looking at yesterday. */
const POLL_MS = 60_000;

type Data = {
  ads: Ad[];
  members: Member[];
  events: Event[];
  todos: Todo[];
  sets: CardSet[];
  assets: Asset[];
  users: BoardUser[];
  art: Art | null;
  transactions: Transaction[];
  finance: Finance | null;
  stats: Stats | null;
  refresh: () => Promise<void>;
  memberName: (id: number | null) => string;
};

const DataCtx = createContext<Data | null>(null);
export const useData = (): Data => {
  const v = useContext(DataCtx);
  if (!v) throw new Error('useData outside provider');
  return v;
};

function DataProvider({ children }: { children: ReactNode }) {
  const [ads, setAds] = useState<Ad[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [sets, setSets] = useState<CardSet[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [users, setUsers] = useState<BoardUser[]>([]);
  const [art, setArt] = useState<Art | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [finance, setFinance] = useState<Finance | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);

  /**
   * Load everything the board shows.
   *
   * Eleven calls used to go out under one Promise.all, which fails whole:
   * a single endpoint refusing — a flaky connection, one route erroring, an
   * extension blocking a URL it dislikes — threw before anything was
   * applied, and the entire board stayed empty. One page's worth of trouble
   * took down nine pages that were fine.
   *
   * Each answer is applied on its own now. A call that fails leaves its
   * previous value in place, so the board goes stale in one corner instead
   * of blank everywhere, and the next poll picks it up. Only a sweep where
   * every single call failed is treated as an error worth raising, because
   * that is the whole server being gone rather than one thing being sick.
   */
  const refresh = useCallback(async () => {
    const [m, e, t, s, a, u, ar, tx, f, st, ad] = await Promise.allSettled([
      api.members(),
      api.events(),
      api.todos(),
      api.sets(),
      api.assets(),
      api.users(),
      api.art(),
      api.transactions(),
      api.finance(),
      api.stats(),
      api.ads(),
    ]);
    const take = <T,>(r: PromiseSettledResult<T>, set: (v: T) => void): boolean => {
      if (r.status !== 'fulfilled') return false;
      set(r.value);
      return true;
    };
    const landed = [
      take(ad, setAds),
      take(m, setMembers),
      take(e, setEvents),
      take(t, setTodos),
      take(s, setSets),
      take(a, setAssets),
      take(u, setUsers),
      take(ar, setArt),
      take(tx, setTransactions),
      take(f, setFinance),
      take(st, setStats),
    ];
    if (!landed.some(Boolean)) {
      throw new Error('refresh_failed');
    }
  }, []);

  /* Three people share this board, so what one of them writes has to reach
     the others. Everything used to be fetched once on mount and never again:
     an advertisement added at seven was invisible to a tab that had been open
     since six, with nothing on screen to say so.

     Refetch when the tab comes back to the front, and slowly while it is
     being looked at. Nothing runs while the tab is hidden — a board left open
     on a laptop lid should not poll all night. */
  useEffect(() => {
    let alive = true;
    let inFlight = false;

    const pull = () => {
      if (inFlight || document.hidden) return;
      inFlight = true;
      void refresh()
        .catch(() => {
          /* A dropped request just means this round is skipped; the next tick
             or the next focus picks it up. Better a slightly stale board than
             one that empties itself on a flaky connection. */
        })
        .finally(() => {
          if (alive) inFlight = false;
        });
    };

    pull();
    const timer = setInterval(pull, POLL_MS);
    // `visibilitychange` covers switching tabs; `focus` covers switching
    // windows, which does not always fire the first.
    document.addEventListener('visibilitychange', pull);
    window.addEventListener('focus', pull);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', pull);
      window.removeEventListener('focus', pull);
    };
  }, [refresh]);

  const memberName = useCallback(
    (id: number | null) => members.find((m) => m.id === id)?.name ?? '—',
    [members],
  );

  const value = useMemo(
    () => ({ ads, members, events, todos, sets, assets, users, art, transactions, finance, stats, refresh, memberName }),
    [ads, members, events, todos, sets, assets, users, art, transactions, finance, stats, refresh, memberName],
  );

  return <DataCtx.Provider value={value}>{children}</DataCtx.Provider>;
}

/* ══ AUTH ════════════════════════════════════════════════════════════ */

type Auth = { user: User | null; setUser: (u: User | null) => void };
const AuthCtx = createContext<Auth>({ user: null, setUser: () => {} });
export const useAuth = (): Auth => useContext(AuthCtx);

/* ══ SHELL ═══════════════════════════════════════════════════════════ */

const NAV: { to: string; label: string; icon: () => ReactNode; end: boolean }[] = [
  { to: '/', label: 'Yfirlit', icon: Icon.grid, end: true },
  { to: '/vidburdir', label: 'Viðburðir', icon: Icon.calendar, end: false },
  { to: '/felagar', label: 'Spilarar', icon: Icon.people, end: false },
  { to: '/utgjold', label: 'Fjármál', icon: Icon.wallet, end: false },
  { to: '/utgafur', label: 'Útgáfur', icon: Icon.book, end: false },
  { to: '/mot', label: 'Mót', icon: Icon.dice, end: false },
  { to: '/eignir', label: 'Eignir', icon: Icon.box, end: false },
  { to: '/auglysingar', label: 'Auglýsingar', icon: Icon.megaphone, end: false },
  { to: '/myndir', label: 'Myndir', icon: Icon.picture, end: false },
  { to: '/verkefni', label: 'Verkefni', icon: Icon.check2, end: false },
];

const TITLES: Record<string, [string, string]> = {
  '/': ['Yfirlit', 'Staðan á félaginu'],
  '/vidburdir': ['Viðburðir', 'Hver kom, hvað var spilað'],
  '/felagar': ['Spilarar', 'Skráðir félagar og gestir'],
  '/utgjold': ['Fjármál', 'Staða sjóðs, tekjur og gjöld'],
  '/utgafur': ['Útgáfur', 'Næstu sett frá Wizards'],
  '/mot': ['Mót', 'Svissnesk pörun og staðan'],
  '/eignir': ['Eignir', 'Það sem félagið á'],
  '/auglysingar': ['Auglýsingar', 'Plaköt fyrir viðburði'],
  '/myndir': ['Myndir', 'Myndasafn félagsins'],
  '/verkefni': ['Verkefni', 'Það sem þarf að klára'],
};

function Shell() {
  const { user, setUser } = useAuth();
  const { ads, events, todos, members, sets, assets } = useData();
  const { pathname } = useLocation();
  const [pwOpen, setPwOpen] = useState(false);
  // Sub-pages (/utgafur/3) keep their section's heading rather than falling
  // all the way back to the generic one.
  const [title, sub] = TITLES[pathname] ?? TITLES['/' + pathname.split('/')[1]] ?? ['Stjórnborð', ''];

  const nextSet = sets
    .map((s) => ({ ...s, d: daysUntil(s.release_date) }))
    .filter((s) => s.d !== null && s.d >= 0)
    .sort((a, b) => (a.d ?? 0) - (b.d ?? 0))[0];

  const tallies: Record<string, number> = {
    '/vidburdir': events.length,
    '/felagar': members.length,
    '/eignir': assets.reduce((n, a) => n + a.quantity, 0),
    '/auglysingar': ads.length,
    '/verkefni': todos.filter((t) => !t.done && t.assigned_user_id === user?.id).length,
  };

  const initials = (user?.display_name ?? '?')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  async function signOut() {
    await api.logout();
    setUser(null);
  }

  return (
    <div className="shell">
      <aside className="rail">
        <div className="brand">
          <span className="brand-mark">
            <Sigil />
          </span>
          <span className="brand-txt">
            <b>Spilafélagið</b>
            <span>Stjórnborð</span>
          </span>
        </div>

        <nav className="nav">
          {NAV.map(({ to, label, icon: I, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => (isActive ? 'on' : '')}>
              <I />
              {label}
              {tallies[to] !== undefined && <span className="tally">{tallies[to]}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="rail-foot">
          <button
            type="button"
            className="who who-btn"
            onClick={() => setPwOpen(true)}
            title="Breyta lykilorði"
          >
            <span className="av">{initials}</span>
            <span className="nm">
              <b>{user?.display_name}</b>
              <span>Breyta lykilorði</span>
            </span>
          </button>
          <button className="signout" onClick={signOut} title="Skrá út" aria-label="Skrá út">
            <Icon.out />
          </button>
        </div>
      </aside>

      <main>
        <header className="top">
          <div className="ttl">
            <h1>{title}</h1>
            <span>{sub}</span>
          </div>
          <div className="next">
            <span className="lbl">Næsta útgáfa</span>
            <span className="v">
              {nextSet ? `${nextSet.d} dagar · ${nextSet.name}` : '—'}
            </span>
          </div>
        </header>

        <Routes>
          <Route path="/" element={<Yfirlit />} />
          <Route path="/vidburdir" element={<Vidburdir />} />
          <Route path="/felagar" element={<Felagar />} />
          <Route path="/utgjold" element={<Utgjold />} />
          <Route path="/utgafur" element={<Utgafur />} />
          <Route path="/utgafur/:id" element={<Utgafa />} />
          <Route path="/mot" element={<Mot />} />
          <Route path="/eignir" element={<Eignir />} />
          <Route path="/auglysingar" element={<Auglysingar />} />
          <Route path="/myndir" element={<Myndir />} />
          <Route path="/verkefni" element={<Verkefni />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {pwOpen && <Lykilord onClose={() => setPwOpen(false)} />}
    </div>
  );
}

/* ══ ROOT ════════════════════════════════════════════════════════════ */

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    api
      .me()
      .then(({ user: u }) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setBooting(false));
  }, []);

  const auth = useMemo(() => ({ user, setUser }), [user]);

  if (booting) return <div className="login-wrap" aria-busy="true" />;

  return (
    <AuthCtx.Provider value={auth}>
      {user ? (
        <DataProvider>
          <BrowserRouter>
            <Shell />
          </BrowserRouter>
        </DataProvider>
      ) : (
        <Login />
      )}
    </AuthCtx.Provider>
  );
}
